import clsx from 'clsx';
import { type KeyboardEvent, useState } from 'react';

import { ExpertRequestType } from '@/__generated__/graphql';
import FAIcon from '@/components/Icon/FAIcon';
import { UpgradeRequiredDialog } from '@/components/UpgradeRequiredDialog';
import config from '@/config';
import { resolveActiveContextGroup } from '@/core/user';
import { isEngagementsActiveForContext } from '@/features/consulting-engagements/gate';
import { useAppSelector } from '@/store';
import { brandPrimary, darkGray, surfaceCard } from '@/theme/colors';

import ExpertRequestWizardPage from './ExpertRequestWizardPage';

// Copy is owner-signed-off against the team-reviewed prototype (CreateType.jsx).
// Card *titles* are the renamed display labels; the er_type enum values are unchanged.
export const COPY = {
  heading: 'How would you like to start working with an expert?',
  intro:
    'On OnFrontiers, expert relationships grow over time. A single consultation can lead to a document review, a scoped project, or even a permanent hire. You can move between them in any direction as you go.',
  recommendedBadge: 'Recommended',
  sectionLabel: 'Other engagement types',
  footer: "You're never locked in. Any engagement can evolve in any direction.",
  consultation: {
    title: 'Consultation',
    desc: 'Book a video call with a vetted expert. The fastest way to get answers, and the easiest way to begin a relationship you can build on.',
    helper: 'Not sure exactly what you need yet? This is where most engagements begin.',
    cta: 'Start a consultation request',
  },
  writtenResponse: {
    title: 'Written Response',
    desc: 'Have a document, a proposal or report, reviewed. Set a time limit; the expert delivers written feedback. No call required.',
    cta: 'Find experts for a written response',
  },
  scopedEngagement: {
    title: 'Scoped Engagement',
    desc: 'Scope a deeper engagement around a defined statement of work. Best when you already know what you need. Takes longer to source.',
    cta: 'Find experts for a scoped engagement',
  },
} as const;

// Selecting a card is the card click; proceeding is the CTA button click.
const handleSelectKey = (e: KeyboardEvent, onSelect: () => void) => {
  // Ignore keys bubbled up from the inner CTA button — only react to the card itself.
  if (e.target !== e.currentTarget) return;
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onSelect();
  }
};

// Selected CTA reads as a filled brand button; unselected mirrors the white
// "Add question" button on the ER details form (surface-card fill + border).
const ctaClasses = (selected: boolean) =>
  clsx(
    'inline-flex items-center gap-8 self-start rounded border px-20 py-10 text-14 font-bold transition-colors',
    selected
      ? 'border-brand-primary bg-brand-primary text-surface-card hover:bg-brand-primary-hover'
      : 'border-surface-border bg-surface-card text-on-surface hover:bg-surface-elevated'
  );

const SelectionRadio = ({ selected }: { selected: boolean }): JSX.Element => (
  <span
    className={clsx(
      'flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-2',
      selected ? 'border-brand-primary' : 'border-surface-border group-hover:border-brand-primary'
    )}
    aria-hidden="true"
  >
    {selected && <span className="h-10 w-10 rounded-full bg-brand-primary" />}
  </span>
);

const IconTile = ({
  icon,
  selected,
  hero,
}: {
  icon: string;
  selected: boolean;
  hero?: boolean;
}): JSX.Element => (
  <div
    className={clsx(
      'flex shrink-0 items-center justify-center',
      hero ? 'h-40 w-40 rounded-lg' : 'h-36 w-36 rounded-md',
      selected ? 'bg-brand-primary' : 'bg-surface-elevated'
    )}
  >
    <FAIcon
      iconSet="far"
      icon={icon}
      size={hero ? 20 : 19}
      color={selected ? surfaceCard : darkGray}
    />
  </div>
);

interface EngagementCardProps {
  hero?: boolean;
  icon: string;
  title: string;
  desc: string;
  helper?: string;
  cta: string;
  recommended?: boolean;
  selected: boolean;
  hidden?: boolean;
  onSelect: () => void;
  onProceed: () => void;
}

const EngagementCard = ({
  hero,
  icon,
  title,
  desc,
  helper,
  cta,
  recommended,
  selected,
  hidden,
  onSelect,
  onProceed,
}: EngagementCardProps): JSX.Element | null => {
  if (hidden) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Select ${title}`}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(e) => handleSelectKey(e, onSelect)}
      className={clsx(
        'group relative cursor-pointer border-2 text-left transition-colors',
        hero ? 'mb-32 rounded-xl p-24' : 'flex flex-col rounded-lg p-20',
        selected
          ? 'border-brand-primary bg-surface-elevated'
          : 'border-surface-border bg-surface-card hover:border-brand-primary hover:bg-surface-elevated'
      )}
    >
      {hero ? (
        // Single aligned row: icon, title, then the badge + radio pinned right.
        // Keeps everything on one line on mobile instead of overflowing.
        <div className="mb-14 flex items-center gap-12">
          <IconTile icon={icon} selected={selected} hero />
          <h2 className="m-0 text-24 font-bold text-on-surface">{title}</h2>
          <div className="ml-auto flex shrink-0 items-center gap-10">
            {recommended && (
              <span className="whitespace-nowrap rounded-full bg-brand-primary px-10 py-4 text-12 font-bold text-surface-card">
                {COPY.recommendedBadge}
              </span>
            )}
            <SelectionRadio selected={selected} />
          </div>
        </div>
      ) : (
        <>
          <span className="absolute right-16 top-16">
            <SelectionRadio selected={selected} />
          </span>
          <div className="mb-12 flex items-center gap-12">
            <IconTile icon={icon} selected={selected} />
            <h3 className="m-0 text-18 font-bold text-on-surface">{title}</h3>
          </div>
        </>
      )}
      <p
        className={clsx(
          'leading-normal',
          hero
            ? 'mb-10 max-w-full text-16 text-on-surface lg:max-w-[70%]'
            : 'mb-20 flex-1 text-14 text-on-surface-muted'
        )}
      >
        {desc}
      </p>
      {hero && helper && (
        <p className="mb-20 max-w-full text-14 leading-normal text-on-surface-muted lg:max-w-[70%]">
          {helper}
        </p>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onProceed();
        }}
        className={ctaClasses(selected)}
      >
        {cta}
        <FAIcon
          iconSet="far"
          icon="arrow-right"
          size={16}
          color={selected ? surfaceCard : brandPrimary}
        />
      </button>
    </div>
  );
};

const SelectType = ({ change, onNext }: any) => {
  const viewer = useAppSelector((state) => state.viewer);
  const userContext = useAppSelector((state) => state.ui.userContext);
  // ENG-5351: gate on the ACTIVE account (the "view as" context, else the default
  // account), NOT an all-groups "any group is SMB" scan — a multi-account user must
  // be classified by the account they're operating in. planType is the entitlement
  // signal (holds even for a pending SMB account with no billing_account yet).
  // The API applies NO server-side er_type entitlement, so this UI gate is the only
  // enforcement and must fail RESTRICTED: when the active context can't be resolved
  // (a stale/non-member userContext, before ENG-5509 revalidates it), fall back to the
  // all-groups scan so an SMB-only user is never silently unlocked.
  const activeGroup = resolveActiveContextGroup(viewer, userContext);
  const isSMB = activeGroup
    ? activeGroup.capabilities?.planType === 'smb'
    : (viewer.groups || []).some((g) => g?.capabilities?.planType === 'smb');
  const isRestricted = !viewer.admin && isSMB;

  // ENG-5670: an account that has the real consulting-engagements product must
  // not be offered the Scoped Engagement card below, which despite its title
  // creates an `er_type` of ConsultingProject (the label everywhere else in the
  // app, see expertrequest/store/index.ts). Two doors to two different things is
  // the correctness problem; the revenue one is that consultations burn credits
  // and engagements are USD-billed, so surfacing the engagement path from inside
  // the ER flow suppresses credit burn.
  //
  // The ACCOUNT question, not the visibility policy: `canViewEngagementSurfaces`
  // returns true for any admin whenever the allowlist is non-empty, so using it
  // here would take the card away from staff in every account context. That
  // would invert `isRestricted` one line above, which deliberately exempts
  // admins from the SMB restriction. Obeying the active view-as context instead
  // means staff lose the card exactly when they are acting as an allowlisted
  // account, and keep it otherwise.
  //
  // Fails OPEN, unlike isRestricted: an unresolvable context shows the card. A
  // card that has worked for years staying visible beats hiding it on a
  // resolution miss. That is also the shape of the known ENG-5680 gap, where a
  // staff view-as of a non-member group does not resolve.
  const engagementsActiveForContext = isEngagementsActiveForContext(viewer, userContext);

  // Derived once and used for BOTH the cards' `hidden` and the grid's column
  // count, so the layout cannot drift away from what actually renders.
  const showWrittenResponse = !config.disableWrittenEngagement;
  const showScopedEngagement = !engagementsActiveForContext;

  const [selected, setSelected] = useState<ExpertRequestType>(ExpertRequestType.Consultation);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');

  const proceed = (type: ExpertRequestType) => {
    change('er_type', type);
    onNext();
  };

  const handleRestrictedClick = (feature: string) => {
    setUpgradeFeature(feature);
    setUpgradeDialogOpen(true);
  };

  // SMB-restricted users can't use the paid types — selecting the card or
  // clicking its CTA both surface the upgrade dialog instead.
  const handlersFor = (type: ExpertRequestType, restricted: boolean, feature: string) =>
    restricted
      ? {
          onSelect: () => handleRestrictedClick(feature),
          onProceed: () => handleRestrictedClick(feature),
        }
      : { onSelect: () => setSelected(type), onProceed: () => proceed(type) };

  return (
    <ExpertRequestWizardPage editName={false} title={COPY.heading} subTitle={COPY.intro}>
      <EngagementCard
        hero
        recommended
        icon="phone"
        title={COPY.consultation.title}
        desc={COPY.consultation.desc}
        helper={COPY.consultation.helper}
        cta={COPY.consultation.cta}
        selected={selected === ExpertRequestType.Consultation}
        {...handlersFor(ExpertRequestType.Consultation, false, '')}
      />

      <div className="mb-14 text-12 font-bold uppercase tracking-wider text-on-surface-muted">
        {COPY.sectionLabel}
      </div>
      {/* Two-up only when both cards render. With a fixed `sm:grid-cols-2` the
          survivor of ENG-5670's hidden Scoped Engagement card sat at half width
          with dead space beside it, under a full-width Consultation card. */}
      <div
        className={clsx(
          'grid grid-cols-1 gap-16',
          showWrittenResponse && showScopedEngagement && 'sm:grid-cols-2'
        )}
      >
        <EngagementCard
          icon="book"
          title={COPY.writtenResponse.title}
          desc={COPY.writtenResponse.desc}
          cta={COPY.writtenResponse.cta}
          selected={selected === ExpertRequestType.WrittenReview}
          hidden={!showWrittenResponse}
          {...handlersFor(ExpertRequestType.WrittenReview, isRestricted, 'Written Responses')}
        />
        <EngagementCard
          icon="briefcase"
          title={COPY.scopedEngagement.title}
          desc={COPY.scopedEngagement.desc}
          cta={COPY.scopedEngagement.cta}
          selected={selected === ExpertRequestType.ConsultingProject}
          hidden={!showScopedEngagement}
          {...handlersFor(ExpertRequestType.ConsultingProject, isRestricted, 'Scoped Engagements')}
        />
      </div>

      <div className="mt-24 flex items-center gap-8 text-on-surface-muted">
        <FAIcon iconSet="far" icon="history" size={16} color={darkGray} />
        <span className="text-14 text-on-surface-muted">{COPY.footer}</span>
      </div>

      <UpgradeRequiredDialog
        open={upgradeDialogOpen}
        feature={upgradeFeature}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </ExpertRequestWizardPage>
  );
};

SelectType.title = 'Select Request Type';

export default SelectType;
